import { useMemo } from 'react';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import {
  type CarouselItems,
  type ProductDetailsContent,
  type ProductDetailsMappedContent,
} from '../../ProductDetails.types.ts';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsContent = (params: Params) => {
  const { baseProductSlug } = params;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const searchParams = useMemo(
    () => ({ 'fields.baseProductSlug': baseProductSlug }),
    [baseProductSlug],
  );

  const { data, fetching } =
    useContentfulContentTypeEntry<ProductDetailsContent>({
      contentType: PRODUCT_DETAILS_CONTENT_TYPE,
      searchParams,
    });

  // ─── Derived Data ────────────────────────────────────────────────────

  const superIngredients = useMemo(() => {
    const entries = data?.fields?.superIngredients ?? [];

    return entries.map((ingredient) => ingredient.fields);
  }, [data?.fields?.superIngredients]);

  const mappedCarousel = useMemo<CarouselItems>(() => {
    const carousel = data?.fields?.carousel ?? [];

    return carousel.map((asset) => {
      const media = asset.fields.imageVideo;

      const isVideo = media.fields.file.contentType.startsWith('video');
      const source = media.fields.file.url;
      const staticSource = asset.fields.staticImage?.fields?.file?.url;
      const alt = media.fields.title ?? media.fields.description;

      return { type: isVideo ? 'video' : 'image', source, staticSource, alt };
    });
  }, [data?.fields?.carousel]);

  const mappedContent = useMemo<ProductDetailsMappedContent>(() => {
    return {
      subheading: data?.fields?.subheading,
      description: data?.fields?.description,
      superIngredients,
      carousel: mappedCarousel,
      mobileHeaderBackgroundColor: data?.fields?.mobileHeaderBackgroundColor,
      mobileHeaderBackgroundImage: data?.fields?.mobileHeaderBackgroundImage,
      mobileHeaderForegroundColor: data?.fields?.mobileHeaderForegroundColor,
      nutritionistPhoto: data?.fields?.nutritionistPhoto,
      nutritionistTitle: data?.fields?.nutritionistTitle,
      nutritionistLogo: data?.fields?.nutritionistLogo,
      nutritionistLogoWidth: data?.fields?.nutritionistLogoWidth,
      nutritionistLogoHeight: data?.fields?.nutritionistLogoHeight,
    };
  }, [
    data?.fields?.subheading,
    data?.fields?.description,
    data?.fields?.mobileHeaderBackgroundColor,
    data?.fields?.mobileHeaderBackgroundImage,
    data?.fields?.mobileHeaderForegroundColor,
    data?.fields?.nutritionistPhoto,
    data?.fields?.nutritionistTitle,
    data?.fields?.nutritionistLogo,
    data?.fields?.nutritionistLogoWidth,
    data?.fields?.nutritionistLogoHeight,
    superIngredients,
    mappedCarousel,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    fetching,
    mappedContent,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PRODUCT_DETAILS_CONTENT_TYPE = 'productDetails';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
};
